import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PlayCircle } from 'lucide-react';
import { get } from '@/api/client';
import { getYouTubeId } from '@/lib/youtube';

/**
 * YouTube 미리보기 카드 — 썸네일 + 타이틀 + 채널명.
 *
 * 본문에 저장된 YouTube 링크를 카드로 렌더링한다. (에디터/상세 페이지 공용)
 * oEmbed 는 브라우저 CORS 로 직접 호출 불가 → 백엔드 프록시(/youtube/oembed) 사용.
 * 클릭 시 새 탭에서 원본 영상으로 이동.
 */

interface OEmbed {
  videoId: string;
  title: string;
  authorName: string;
  thumbnailUrl: string;
  url: string;
}

export function YouTubePreview({ url }: { url: string }) {
  const id = getYouTubeId(url);

  /* 썸네일은 oEmbed 응답 대신 videoId 기반 표준 URL 을 직접 사용한다.
     maxresdefault(고화질)부터 시도하고, 없으면 hq → mq 순으로 단계적 폴백.
     (maxresdefault 는 일부 영상에 없어 404 가 나므로 폴백 필수) */
  const [thumbStage, setThumbStage] = useState(0);
  const thumbCandidates = id
    ? [
        `https://img.youtube.com/vi/${id}/maxresdefault.jpg`,
        `https://img.youtube.com/vi/${id}/hqdefault.jpg`,
        `https://img.youtube.com/vi/${id}/mqdefault.jpg`,
      ]
    : [];

  const { data, isLoading } = useQuery({
    queryKey: ['youtube', 'oembed', id],
    queryFn: () => get<OEmbed>('/youtube/oembed', { url }),
    enabled: !!id,
    staleTime: 60 * 60_000, // 1시간
    retry: 1,
  });

  if (!id) return null;

  // 로딩 중 — 썸네일 자리 스켈레톤
  if (isLoading || !data) {
    return (
      <div className="my-3 flex max-w-md animate-pulse overflow-hidden rounded-xl border border-[var(--color-border)]">
        <div className="aspect-video w-40 shrink-0 bg-gray-200" />
        <div className="flex-1 space-y-2 p-3">
          <div className="h-3 w-3/4 rounded bg-gray-200" />
          <div className="h-3 w-1/2 rounded bg-gray-200" />
        </div>
      </div>
    );
  }

  return (
    <a
      href={data.url}
      target="_blank"
      rel="noopener noreferrer nofollow"
      className="my-3 flex max-w-md overflow-hidden rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] no-underline transition-shadow hover:shadow-md"
    >
      <div className="relative w-40 shrink-0">
        <img
          src={thumbCandidates[thumbStage]}
          alt={data.title || 'YouTube 영상'}
          loading="lazy"
          crossOrigin="anonymous"
          className="aspect-video h-full w-full object-cover"
          /* maxres → hq → mq 단계적 폴백. 현재 단계 실패 시 다음 단계로. */
          onError={() => {
            setThumbStage((s) => (s < thumbCandidates.length - 1 ? s + 1 : s));
          }}
        />
        <span className="absolute inset-0 flex items-center justify-center bg-black/20">
          <PlayCircle className="h-8 w-8 text-white drop-shadow" />
        </span>
      </div>
      <div className="flex min-w-0 flex-1 flex-col justify-center gap-1 p-3">
        <p className="line-clamp-2 text-sm font-semibold text-[var(--color-text)]">
          {data.title || '제목 없음'}
        </p>
        {data.authorName && (
          <p className="truncate text-xs text-[var(--color-text-sub)]">
            {data.authorName}
          </p>
        )}
      </div>
    </a>
  );
}

/** 본문에서 추출한 여러 YouTube URL 을 카드 목록으로 렌더 */
export function YouTubePreviewList({ urls }: { urls: string[] }) {
  if (urls.length === 0) return null;
  return (
    <div className="not-prose flex flex-col">
      {urls.map((u, i) => (
        <YouTubePreview key={`${u}-${i}`} url={u} />
      ))}
    </div>
  );
}
